/**
 * @name UserAppBar
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Este componente é o app bar do layout do usuário.
 * @param {UserAppBarProps.handleDrawerToggle} props.handleDrawerToggle - Função para abrir e fechar o menu lateral.
 * @returns {JSX.Element} - O JSX do componente.
 * @package src/components/layouts/UserLayout/components/UserAppBar.tsx
 */

import AccountCircle from '@mui/icons-material/AccountCircle';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { Divider, Grid, ListItemIcon } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../contexts/AlertContext';
import useUserContext from '../../../../contexts/UserContext/useUserContext';
import useThemeContext from '../../../../themes/hooks/useThemeContext';
import Logo from '../../../common/Logo';

interface UserAppBarProps {
     handleDrawerToggle: () => void;
}

const UserAppBar: React.FC<UserAppBarProps> = ({ handleDrawerToggle }) => {
     const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
     const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);
     const { theme, toggleTheme } = useThemeContext();
     const navigate = useNavigate();
     const { signOut } = useUserContext();
     const { showSuccess } = useAlert();
     const isMenuOpen = Boolean(anchorEl);
     const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

     const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
     };

     const handleMobileMenuClose = () => {
          setMobileMoreAnchorEl(null);
     };

     const handleMenuClose = () => {
          setAnchorEl(null);
          handleMobileMenuClose();
     };

     const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
          setMobileMoreAnchorEl(event.currentTarget);
     };

     const handleNavigateToAccount = () => {
          navigate('/minha-conta');
          handleMenuClose();
     };

     const handleNavigateToSettings = () => {
          navigate('/configuracoes');
          handleMenuClose();
     };

     const handleLogout = async () => {
          try {
               await signOut();
               showSuccess('Logout realizado com sucesso!');
               navigate('/auth/login');
          } catch (error) {
               console.error('Erro ao fazer logout:', error);
          }
          handleMenuClose();
     };

     const handleToggleTheme = () => {
          toggleTheme();
          handleMenuClose();
     };

     const menuId = 'primary-account-menu';
     const renderMenu = (
          <Menu
               anchorEl={anchorEl}
               anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
               }}
               id={menuId}
               keepMounted
               transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
               }}
               open={isMenuOpen}
               onClose={handleMenuClose}
          >
               <MenuItem onClick={handleNavigateToAccount}>
                    <ListItemIcon>
                         <PersonIcon fontSize='small' />
                    </ListItemIcon>
                    Minha Conta
               </MenuItem>
               <MenuItem onClick={handleToggleTheme}>
                    <ListItemIcon>{theme.palette.mode === 'dark' ? <LightModeIcon fontSize='small' /> : <DarkModeIcon fontSize='small' />}</ListItemIcon>
                    {theme.palette.mode === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
               </MenuItem>
               <Divider />
               <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                         <LogoutIcon fontSize='small' />
                    </ListItemIcon>
                    Sair
               </MenuItem>
          </Menu>
     );

     const mobileMenuId = 'primary-account-menu-mobile';
     const renderMobileMenu = (
          <Menu
               anchorEl={mobileMoreAnchorEl}
               anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
               }}
               id={mobileMenuId}
               keepMounted
               transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
               }}
               open={isMobileMenuOpen}
               onClose={handleMobileMenuClose}
          >
               <MenuItem onClick={handleNavigateToAccount}>
                    <ListItemIcon>
                         <PersonIcon fontSize='small' />
                    </ListItemIcon>
                    Minha Conta
               </MenuItem>
               <MenuItem onClick={handleNavigateToSettings}>
                    <ListItemIcon>
                         <SettingsIcon fontSize='small' />
                    </ListItemIcon>
                    Configurações
               </MenuItem>
               <MenuItem onClick={handleToggleTheme}>
                    <ListItemIcon>{theme.palette.mode === 'dark' ? <LightModeIcon fontSize='small' /> : <DarkModeIcon fontSize='small' />}</ListItemIcon>
                    {theme.palette.mode === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
               </MenuItem>
               <Divider />
               <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                         <LogoutIcon fontSize='small' />
                    </ListItemIcon>
                    Sair
               </MenuItem>
          </Menu>
     );

     return (
          <Box color={theme.palette.background.paper}>
               <AppBar
                    position='static'
                    color='transparent'
               >
                    <Toolbar>
                         <IconButton
                              size='large'
                              edge='start'
                              color='default'
                              aria-label='open drawer'
                              onClick={handleDrawerToggle}
                              sx={{ mr: 2 }}
                         >
                              <MenuIcon />
                         </IconButton>
                         <Grid
                              container
                              direction={'row'}
                              justifyContent={'center'}
                         >
                              <Logo
                                   alt={'Logo'}
                                   width={60}
                              />
                         </Grid>
                         <Box sx={{ flexGrow: 1 }} />
                         <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                              <IconButton
                                   size='large'
                                   edge='end'
                                   aria-label='account of current user'
                                   aria-controls={menuId}
                                   aria-haspopup='true'
                                   onClick={handleProfileMenuOpen}
                                   color='default'
                              >
                                   <AccountCircle />
                              </IconButton>
                         </Box>
                         <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                              <IconButton
                                   size='large'
                                   aria-label='show more'
                                   aria-controls={mobileMenuId}
                                   aria-haspopup='true'
                                   onClick={handleMobileMenuOpen}
                                   color='default'
                              >
                                   <MoreIcon />
                              </IconButton>
                         </Box>
                    </Toolbar>
               </AppBar>
               {renderMobileMenu}
               {renderMenu}
          </Box>
     );
};

export default UserAppBar;
